import Link from 'next/link';

import { Icon, IconButton, Menu } from '@zillow/constellation-10';
import { IconMoreVerticalFilled } from '@zillow/constellation-icons';

import { toAppRoute } from '../../constants/urls';

import type { Route } from 'nextjs-routes';
import type {
  AccountViewListingOptionDto,
  AccountViewListingOptionType,
  AccountViewPropertyOptionDto,
  AccountViewPropertyUnitOptionDto,
} from '@zg-rentals/ts-rental-manager-api-client';

export type OverflowMenuItem = {
  label: string;
  href?: Route;
  onSelect?: () => void;
};

type ApiOverflowOption = AccountViewListingOptionDto | AccountViewPropertyOptionDto | AccountViewPropertyUnitOptionDto;

/**
 * Builds overflow menu items from API row options. Uses a `Link` href when `ctaUrl`
 * is a non-empty app path (does not contain `api/web`); otherwise uses `actionOverrides`.
 *
 * Options that resolve to neither a route nor an override are dropped: an item with no
 * action does nothing when clicked, so rendering it just advertises a dead control.
 */
export const buildOverflowMenuItems = (
  options: Array<ApiOverflowOption>,
  {
    actionOverrides = {},
  }: {
    actionOverrides?: Partial<Record<AccountViewListingOptionType, () => void>>;
  } = {},
): Array<OverflowMenuItem> =>
  options.reduce<Array<OverflowMenuItem>>((items, { type, ctaText, ctaUrl }) => {
    const override = actionOverrides[type];
    // TODO: BE will remove the api/web api paths
    const shouldNavigate = Boolean(ctaUrl) && !ctaUrl.includes('api/web');

    if (shouldNavigate) {
      items.push({ label: ctaText, href: toAppRoute(ctaUrl) });
    } else if (override) {
      items.push({ label: ctaText, onSelect: override });
    }

    return items;
  }, []);

type Props = {
  items: Array<OverflowMenuItem>;
  triggerLabel: string;
  disabled?: boolean;
};

const OverflowMenu = ({ items, triggerLabel, disabled = false }: Props) => {
  const stopRowNavigation = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  if (items.length === 0 && !disabled) {
    return null;
  }

  return (
    <Menu.Root>
      <Menu.Trigger>
        <IconButton
          aria-label={triggerLabel}
          tone="neutral"
          emphasis="bare"
          title={triggerLabel}
          size="md"
          disabled={disabled}
          onClick={stopRowNavigation}
          data-testid="OverflowMenu-Trigger"
        >
          <Icon render={<IconMoreVerticalFilled />} />
        </IconButton>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content>
          {items.map((item) =>
            item.href ? (
              <Menu.Item key={item.label} asChild onClick={(event) => event.stopPropagation()}>
                <Link href={item.href}>{item.label}</Link>
              </Menu.Item>
            ) : (
              <Menu.Item
                key={item.label}
                onClick={(event) => {
                  stopRowNavigation(event);
                  item.onSelect?.();
                }}
              >
                <Menu.ItemLabel>{item.label}</Menu.ItemLabel>
              </Menu.Item>
            ),
          )}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
};

export { OverflowMenu };
